import React, { useState, useEffect } from "react";
import axios from "axios";
import { Clock, Info, MapPin, Wifi, UserX, Save } from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { SettingsCard, SettingsField, SettingsInput, Toggle } from "./settingsUi";

const defaultPolicies = {
  late_after_minutes: "",
  min_overtime_minutes: "",
  auto_mark_out: false,
  require_location_validation: false,
  require_network_validation: false,
};

// A toggle row inside a card (icon tile + title/desc + switch on the right).
const ToggleRow = ({ icon: Icon, iconColor = T.orange, iconBg = T.orange50, title, desc, checked, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "16px 0",
    }}
  >
    <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={18} color={iconColor} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>{title}</div>
      <div style={{ font: `400 12px ${T.font}`, color: T.fg5, marginTop: 2 }}>{desc}</div>
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

const AttendanceSettings = () => {
  const [policies, setPolicies] = useState(defaultPolicies);
  const [dirty, setDirty] = useState(false);
  const token = localStorage.getItem("token");
  const baseURL = API_URL;

  useEffect(() => {
    axios
      .get(`${baseURL}/api/attendance/policies`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPolicies(res.data ?? defaultPolicies))
      .catch(() => setPolicies(defaultPolicies));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (key, value) => {
    setPolicies((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    await axios.put(`${baseURL}/api/attendance/policies`, policies, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setDirty(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Info banner */}
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "16px 18px",
          borderRadius: 14,
          background: T.orange50,
          border: `1px solid ${T.orangeEdge}`,
        }}
      >
        <Info size={18} color={T.orange} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>
            Flexible Attendance by Default
          </div>
          <div style={{ font: `400 12px/1.6 ${T.font}`, color: T.fg4, marginTop: 3 }}>
            Location and network validation are <b>disabled by default</b>, allowing employees to
            mark attendance from anywhere. Enable these features below only if your company policy
            requires stricter validation.
          </div>
        </div>
      </div>

      {/* Timing & Rules */}
      <SettingsCard
        icon={Clock}
        title="Timing &amp; Rules"
        subtitle="Configure core attendance timing parameters"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <SettingsField
            label="Grace Period (minutes)"
            help="Allow employees to check in without being marked late"
          >
            <SettingsInput
              type="number"
              min="0"
              placeholder="15"
              value={policies.late_after_minutes ?? ""}
              onChange={(e) => update("late_after_minutes", e.target.value)}
            />
          </SettingsField>
          <SettingsField
            label="Overtime Threshold (minutes)"
            help="Working hours beyond this will be considered overtime"
          >
            <SettingsInput
              type="number"
              min="0"
              placeholder="480"
              value={policies.min_overtime_minutes ?? ""}
              onChange={(e) => update("min_overtime_minutes", e.target.value)}
            />
          </SettingsField>
        </div>

        <div style={{ borderTop: `1px solid ${T.divider}`, marginTop: 8 }}>
          <ToggleRow
            icon={UserX}
            title="Auto-mark absent"
            desc="Automatically mark employees absent if they don't check in"
            checked={!!policies.auto_mark_out}
            onChange={(v) => update("auto_mark_out", v)}
          />
        </div>
      </SettingsCard>

      {/* Location validation */}
      <SettingsCard icon={MapPin} title="Location Validation" subtitle="Restrict attendance marking to within office premises" bodyPadding="6px 24px"
        actions={
          <Toggle
            checked={!!policies.require_location_validation}
            onChange={(v) => update("require_location_validation", v)}
          />
        }
      >
        <div style={{ font: `400 12px ${T.font}`, color: T.fg5, padding: "12px 0" }}>
          When enabled, employees can only mark attendance while inside the configured office
          location radius.
        </div>
      </SettingsCard>

      {/* Network validation */}
      <SettingsCard icon={Wifi} title="Network Validation" subtitle="Restrict attendance marking to office network IP addresses" bodyPadding="6px 24px"
        actions={
          <Toggle
            checked={!!policies.require_network_validation}
            onChange={(v) => update("require_network_validation", v)}
          />
        }
      >
        <div style={{ font: `400 12px ${T.font}`, color: T.fg5, padding: "12px 0" }}>
          When enabled, attendance can only be marked from devices on an approved office network.
        </div>
      </SettingsCard>

      {/* Save bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          background: T.bgCard,
          border: `1px solid ${T.orangeEdge}`,
          borderRadius: T.rXl,
          padding: "18px 24px",
          boxShadow: "0 6px 16px -10px rgba(245,75,1,0.4)",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>Ready to save changes?</div>
          <div style={{ font: `400 13px ${T.font}`, color: T.fg5, marginTop: 2 }}>
            All attendance policy configurations will be updated
          </div>
        </div>
        {dirty && (
          <span style={{ font: `500 12px ${T.font}`, color: T.warn }}>Unsaved changes</span>
        )}
        <button
          onClick={save}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 10, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `700 14px ${T.font}`, boxShadow: T.shadowBrand }}
        >
          <Save size={16} /> Save Settings
        </button>
      </div>
    </div>
  );
};

export default AttendanceSettings;
